import {
  forwardRef,
  useImperativeHandle,
  useContext,
  useMemo,
  useEffect,
  useState
} from "react";
import * as yup from "yup";
import PropTypes from "prop-types";
import cx from "classnames";
import cloneDeep from "lodash/cloneDeep";

import { InputAdornment, Tooltip } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Grid from "@mui/material/Grid";
import InfoIcon from "@mui/icons-material/Info";
import makeStyles from "@mui/styles/makeStyles";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useShallow } from "zustand/shallow";

import styles from "assets/jss/default/components/caseInfoStyle.js";
import { TextField } from "components/TextField";
import { useFormAutoValidation } from "hooks/useFormValidation";
import { CaseStatusFields } from "components/CaseManagement/CaseDetail/CaseStatusFields";
import { MetaDataTable } from "components/CaseManagement/CaseDetail/MetaDataTable/MetaDataTable";
import NotificationsIndicatorButton from "components/CaseManagement/CaseDetail/NotificationsIndicatorButton";
import { UserDropdown } from "components/UserDropdown";
import { FailureTypeField } from "components/CaseManagement/CaseDetail/FailureTypeField";
import { UsersContext } from "contexts/UsersContext";
import { useGetMachineById } from "hooks/machines/useGetMachineById";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import { AddCaseLabel } from "components/CaseManagement/CaseDetail/AddCaseLabel";
import { ProjectDropdown } from "components/CaseManagement/CaseForms/ProjectDropdown";
import { MultilineTextField } from "components/MultilineTextField";
import { useSitesQuery } from "hooks/queries/useSitesQuery";
import { FeaturesContext } from "contexts/FeaturesContext";
import { useCaseStore } from "stores/case.store";
import { UNASSIGNED } from "core/constants";
import { PrintCasesDialog } from "components/CaseManagement/PrintCasesDialog";
import { CaseLinks } from "components/CaseManagement/CaseDetail/CaseLinks/CaseLinks";

const useStyles = makeStyles(styles);
const validationSchema = yup.object({
  description: yup
    .string()
    .label("Description")
    .required(),
  failure_mode_name: yup
    .string()
    .label("Failure Mode")
    .max(100, "100 character count reached"),
  recommendation: yup.string().label("Recommendation"),
  estimated_saving: yup.number().label("Estimated Saving"),
  assignee: yup
    .number()
    .label("Assignee")
    .nullable(true),
  status: yup
    .number()
    .label("Status")
    .required(),
  component: yup
    .number()
    .label("Component")
    .required(),
  priority: yup
    .number()
    .label("Priority")
    .required(),
  labels: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        name: yup.string().required()
      })
    )
    .label("Labels")
    .default([]),
  attributes: yup
    .object()
    .test(
      "is-valid-attributes-object",
      "Attributes must be an object with valid attribute entries",
      value => {
        if (value == null) return true;
        if (typeof value !== "object" || Array.isArray(value)) return false;
        // Each value should be an object with required keys and correct types
        return Object.values(value).every(
          attr =>
            attr &&
            typeof attr === "object" &&
            (typeof attr.value === "string" ||
              typeof attr.value === "number") &&
            typeof attr.type_id === "number" &&
            typeof attr.type_name === "string"
        );
      }
    )
    .label("Attributes")
    .default({})
});

/**
 * A form for editing an existing case.
 * @param caseModel the data model of the case
 * @param onFormChange the callback which indicates a change in the form
 * @param onFormDirtyChange the callback which indicates when form dirty state changes
 * @param failureTypes
 * @returns {JSX.Element}
 */
const CaseDetailsForm = forwardRef(function CaseDetailsForm(
  { caseModel, onFormDirtyChange },
  ref
) {
  const formik = useFormAutoValidation(caseModel, null, validationSchema);

  const [showPrintCaseDialog, setShowPrintCaseDialog] = useState(false);

  const setIsCaseDetailFromValid = useCaseStore(
    useShallow(state => state.setIsCaseDetailFromValid)
  );

  useEffect(() => {
    setIsCaseDetailFromValid(formik.isValid);
  }, [formik.isValid, setIsCaseDetailFromValid]);

  useEffect(() => {
    if (onFormDirtyChange) {
      onFormDirtyChange(formik.dirty);
    }
  }, [formik.dirty, onFormDirtyChange]);

  const { isLoading: siteLoading, data: sites } = useSitesQuery();
  const {
    loading: usersLoading,
    error: usersError,
    nonServiceAccountUsers
  } = useContext(UsersContext);

  const { loading: machineLoading, machine } = useGetMachineById(
    caseModel.machine
  );

  const { response: failureTypes } = useFetchGetV2("failure_types?size=99999");

  const site = useMemo(
    () => sites?.results.find(({ id }) => id === machine?.site),
    [sites, machine?.site]
  );

  const { hasFeature } = useContext(FeaturesContext);

  // TODO: event.target.value and saving being cast as number repeatedly
  const setEstimatedSaving = event => {
    const saving = Number(event.target.value);

    if (Number.isNaN(Number(saving))) {
      formik.setFieldError(
        "estimated_saving",
        "Only numerical values can be used for estimated saving"
      );
    } else if (Number(event.target.value) < 0) {
      formik.setFieldError(
        "estimated_saving",
        "Estimated Saving cannot be negative"
      );
    } else {
      formik.setFieldValue("estimated_saving", event.target.value);
    }
  };

  const siteName = site?.name ?? "";
  const machineName = machine?.name ?? "";

  const classes = useStyles();

  const handleProjectChange = selectedProject => {
    formik.setFieldValue(
      "project",
      selectedProject ? selectedProject.id : null
    );
    formik.setFieldValue(
      "project_name",
      selectedProject ? selectedProject.name : null
    );
  };

  const handleCaseAssigneeChange = assigneeId => {
    formik.setFieldValue("assignee", assigneeId ? assigneeId : null);
  };

  const handlePrintCase = () => {
    setShowPrintCaseDialog(true);
  };

  const handleStatusFieldsChange = ({ model }) => {
    formik.setValues({
      ...formik.values,
      ...model
    });
  };

  const handleLabelsChange = ({ model }) => {
    formik.setFieldValue("labels", model.labels);
  };

  const handleMetaDataChange = ({ model, deleted }) => {
    const newAttrs = model.attributes || {};

    const updatedAttrs = cloneDeep(newAttrs);

    // setting the field value *should* trigger dirty state
    formik.setFieldValue("attributes", updatedAttrs, true);

    if (deleted && onFormDirtyChange) {
      // force dirty state notification
      setTimeout(() => {
        onFormDirtyChange(true);
      }, 0);
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: formik.submitForm,
    getValues: () => formik.values,
    isValid: formik.isValid,
    isSubmitting: formik.isSubmitting
  }));

  return (
    <>
      <Box sx={{ p: 2, pb: 0 }}>
        <Grid container direction="column" gap={1}>
          <Grid container item direction="row" justifyContent="space-between">
            <Grid item>
              {siteLoading || machineLoading || !machine || !site ? (
                <Skeleton variant="rectangular" length={20} width={200} />
              ) : (
                <Typography variant="h3" className={classes.caseTitle}>
                  {siteName} &gt; {machineName}
                </Typography>
              )}
            </Grid>
            <Grid item>
              <Grid container item direction="row">
                <Grid item>
                  <Stack direction="row" gap={1}>
                    <Button
                      variant="outlined"
                      data-qa="CM-Button-Case-Print"
                      onClick={handlePrintCase}
                      startIcon={<FileDownloadIcon />}
                    >
                      Report
                    </Button>

                    <NotificationsIndicatorButton caseId={caseModel.id} />
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item>
            <CaseStatusFields
              caseModel={formik.values}
              onChange={handleStatusFieldsChange}
              scalarConfigs={machine?.scalar_configurations}
            />
          </Grid>
          <Grid item>
            <AddCaseLabel
              data-qa="CP-Add-Label-Dropdown"
              caseModel={formik.values}
              labels={formik.values.labels ?? []}
              onChange={handleLabelsChange}
            />
          </Grid>
        </Grid>
      </Box>

      <Accordion defaultExpanded disableGutters>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          data-qa="CP-Accordion-Case-Summary"
        >
          <Typography style={{ marginBottom: 0 }} variant="h3">
            Summary
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container alignItems="flex-start">
            <Grid item xs={12}>
              {hasFeature("projects") && (
                <ProjectDropdown
                  data-qa="CP-Add-Project-Dropdown"
                  onChange={handleProjectChange}
                  projectID={caseModel.project}
                />
              )}
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              className={cx([classes.verticalGap, classes.removePaddingSides])}
            >
              {usersLoading ? (
                <Skeleton variant="rectangular" length={20} width={200} />
              ) : (
                <UserDropdown
                  label="Assignee"
                  users={nonServiceAccountUsers}
                  unassignedValue={UNASSIGNED}
                  error={usersError}
                  helperText={usersError && "Error getting users."}
                  disabled={usersLoading}
                  selectedUserId={formik.values.assignee}
                  onChangeSelectedUserId={handleCaseAssigneeChange}
                  avatarTooltip={
                    "Assignee is the person responsible for the next action on the case"
                  }
                />
              )}
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              className={cx([classes.verticalGap, classes.removePaddingSides])}
            >
              <FailureTypeField
                formik={formik}
                failureTypes={failureTypes?.results ?? []}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              className={cx([classes.verticalGap, classes.removePaddingSides])}
            >
              <MultilineTextField
                id="description"
                required
                name="description"
                label="Description"
                className={classes.inputField}
                value={formik.values.description}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
                onChange={formik.handleChange}
                data-qa="CP-Text-Case-Description"
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              className={cx([classes.verticalGap, classes.removePaddingSides])}
            >
              <MultilineTextField
                id="recommendation"
                name="recommendation"
                label="Recommendation"
                className={classes.inputField}
                value={formik.values.recommendation}
                error={
                  formik.touched.recommendation &&
                  Boolean(formik.errors.recommendation)
                }
                helperText={
                  formik.touched.recommendation && formik.errors.recommendation
                }
                onChange={formik.handleChange}
                data-qa="CP-Text-Case-Recommendation"
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              className={cx([classes.verticalGap, classes.removePaddingSides])}
            >
              <TextField
                id="estimated_saving"
                name="estimated_saving"
                label="Estimated Saving"
                className={classes.inputField}
                value={formik.values.estimated_saving}
                error={
                  formik.touched.estimated_saving &&
                  Boolean(formik.errors.estimated_saving)
                }
                helperText={
                  formik.touched.estimated_saving &&
                  formik.errors.estimated_saving
                }
                onChange={event => setEstimatedSaving(event)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title="Estimated saving is calculated automatically based on the component associated with this case"
                        aria-label="Estimated saving info"
                      >
                        <InfoIcon className={classes.infoIcon} />
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
                data-qa="CP-Text-Case-Estimated-Savings"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography style={{ marginBottom: 0 }} variant="h3">
            Links
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CaseLinks caseId={caseModel.id} />
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          data-qa="CP-Accordion-Attributes"
        >
          <Typography style={{ marginBottom: 0 }} variant="h3">
            Attributes
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="row" alignItems="flex-start">
            <Grid item xs={12} md={12}>
              <MetaDataTable
                caseModel={{
                  ...caseModel,
                  attributes: formik.values.attributes
                }}
                onMetaDataChanged={handleMetaDataChange}
                variant="dense"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      {showPrintCaseDialog && (
        <PrintCasesDialog
          caseIds={[caseModel.id]}
          isOpen={showPrintCaseDialog}
          onClose={() => setShowPrintCaseDialog(false)}
        />
      )}
    </>
  );
});

export default CaseDetailsForm;

CaseDetailsForm.propTypes = {
  caseModel: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onFormDirtyChange: PropTypes.func
};

CaseDetailsForm.defaultProps = {
  caseModel: null
};
